import { getImageProps } from 'next/image';
import { useTranslations } from 'next-intl';
import { type SourceHTMLAttributes, useMemo, useRef } from 'react';

import type { ImageProps } from './types';
import { getSrcUrl } from './utils';

export interface ArtDirectionImageProps extends Omit<ImageProps, 'src'> {
  readonly srcSet: SourceHTMLAttributes<HTMLSourceElement>[];
}

const ArtDirectionImage = ({
  className,
  srcSet,
  alt = '',
  format = 'jpg',
  localizedAlt,
  ...props
}: ArtDirectionImageProps): JSX.Element => {
  const t = useTranslations();
  const imagePropsRef = useRef<ImageProps | object>({});
  const sources = useMemo((): (SourceHTMLAttributes<HTMLSourceElement> & {
    key: string;
  })[] => {
    return srcSet.map(({ media, src }, i) => {
      if (!src) return { key: i.toString(), media, srcSet: '' };
      const {
        props: { srcSet: _srcSet, ...rest },
      } = getImageProps({
        alt,
        src: getSrcUrl(src, format),
        ...props,
      });
      imagePropsRef.current = rest;

      return {
        key: src + media,
        media,
        srcSet: _srcSet,
      };
    });
  }, [srcSet, format, alt, props]);

  return (
    <picture data-cy="art-direction-image">
      {sources.map(({ key, media, srcSet: _srcSet }) => (
        <source
          data-cy="art-direction-image-source"
          key={key}
          media={media}
          srcSet={_srcSet}
        />
      ))}
      <img
        {...imagePropsRef.current}
        alt={localizedAlt ? t(localizedAlt) : alt}
        data-cy="art-direction-image-img"
        className={className}
      />
    </picture>
  );
};

export default ArtDirectionImage;
